import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { isNil } from 'lodash';
import { filter } from 'rxjs/operators';

import { SeoRouteData } from './interfaces';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  ///////////////////////////////////////////////////////////////////////
  // Services
  ///////////////////////////////////////////////////////////////////////

  private readonly meta = inject(Meta);

  private readonly router = inject(Router);

  private readonly title = inject(Title);

  ///////////////////////////////////////////////////////////////////////
  // State
  ///////////////////////////////////////////////////////////////////////

  private isInitialized = false;

  ///////////////////////////////////////////////////////////////////////
  // Init
  ///////////////////////////////////////////////////////////////////////

  public init(): void {
    if (this.isInitialized) {
      return;
    }
    this.isInitialized = true;

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.updateSeoDataFromRoute();
    });

    this.updateSeoDataFromRoute();
  }

  ///////////////////////////////////////////////////////////////////////
  // Set Title & Description
  ///////////////////////////////////////////////////////////////////////

  public setTitle(title: string): void {
    this.title.setTitle(title);
  }

  public setDescription(description: string): void {
    this.meta.updateTag({ name: 'description', content: description });
  }

  public removeDescription(): void {
    this.meta.removeTag('name="description"');
  }

  ///////////////////////////////////////////////////////////////////////
  // Private Methods
  ///////////////////////////////////////////////////////////////////////

  private updateSeoDataFromRoute(): void {
    const seoRouteData = this.getSeoDataFromActiveRoute();

    if (isNil(seoRouteData)) {
      this.setTitle('HelplineSoftware.com');
      this.removeDescription();
      return;
    }

    this.setTitle(seoRouteData.title);
    this.setDescription(seoRouteData.description);
  }

  private getSeoDataFromActiveRoute(): SeoRouteData | undefined {
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    let seoRouteData: SeoRouteData | undefined;

    while (!isNil(route)) {
      seoRouteData = route.data?.seo ?? seoRouteData;
      route = route.firstChild;
    }

    return seoRouteData;
  }
}
